import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { dealerPortalService } from "@/services/dealerPortal/dealerPortalService";
import { MediaService } from "@/services/media/mediaService";
import type {
  DealerPortalCompany,
  DealerPortalCustomer,
  DealerPortalVehicle,
  DealerPortalCustomerJourney,
  DealerPortalColleague,
} from "@/types/dealerPortal";

const DEALER_PORTAL_KEY = "dealer-portal";

export const JOURNEY_PAGE_SIZES = [10, 25, 50] as const;
export const DEFAULT_JOURNEY_PAGE_SIZE = 25;
export const VEHICLES_PAGE_SIZES = [10, 25, 50] as const;
export const DEFAULT_VEHICLES_PAGE_SIZE = 25;
export const CUSTOMERS_PAGE_SIZES = [10, 25, 50] as const;
export const DEFAULT_CUSTOMERS_PAGE_SIZE = 25;

export interface UseDealerPortalOptions {
  /** Om angiven kör getCustomer(id) */
  customerId?: string | null;
  /** Om angiven kör getVehicle(id) */
  vehicleId?: string | null;
  /** Om angiven kör getJourney(id) */
  journeyId?: string | null;
  /** Kör getJourneys() när true (default true) */
  journeysEnabled?: boolean;
  /** Kör getJourneysByStatus när alla tre är satta */
  journeyStatus?: string | null;
  journeyPage?: number;
  journeyPageSize?: number;
  /** Sökfilter för fordonslistan (används vid klick på Sök) */
  vehiclesSearch?: string | null;
  /** Statusfilter för fordonslistan (en status eller null = alla) */
  vehiclesStatus?: string | null;
  /** Sidnummer för fordonslistan (server-side pagination) */
  vehiclesPage?: number;
  /** Sidstorlek för fordonslistan */
  vehiclesPageSize?: number;
  /** Sidnummer för kundlistan (server-side pagination) */
  customersPage?: number;
  /** Sidstorlek för kundlistan */
  customersPageSize?: number;
  /** Fritextsök kunder (personnummer, namn, mejl) – används vid klick på Sök */
  customersSearch?: string | null;
  /** Filtrera kunder på roll: köpare, säljare eller alla */
  customersRole?: "buyer" | "seller" | null;
  /** Filtrera kunder på kundtyp: PRIVATE, BUSINESS, DEALER eller null = alla */
  customersType?: "PRIVATE" | "BUSINESS" | "DEALER" | null;
}

export function useDealerPortal(options?: UseDealerPortalOptions) {
  const queryClient = useQueryClient();
  const customerId = options?.customerId ?? null;
  const vehicleId = options?.vehicleId ?? null;
  const journeyId = options?.journeyId ?? null;
  const journeysEnabled = options?.journeysEnabled !== false;
  const journeyStatus = options?.journeyStatus ?? null;
  const journeyPage = options?.journeyPage ?? 1;
  const journeyPageSize = options?.journeyPageSize ?? DEFAULT_JOURNEY_PAGE_SIZE;
  const vehiclesSearch = options?.vehiclesSearch ?? undefined;
  const vehiclesStatus = options?.vehiclesStatus ?? undefined;
  const vehiclesPage = options?.vehiclesPage ?? 1;
  const vehiclesPageSize = options?.vehiclesPageSize ?? DEFAULT_VEHICLES_PAGE_SIZE;
  const customersPage = options?.customersPage ?? 1;
  const customersPageSize = options?.customersPageSize ?? DEFAULT_CUSTOMERS_PAGE_SIZE;
  const customersSearch = options?.customersSearch ?? undefined;
  const customersRole = options?.customersRole ?? undefined;
  const customersType = options?.customersType ?? undefined;

  // Queries (inga parametrar)
  const company = useQuery({
    queryKey: [DEALER_PORTAL_KEY, "company"],
    queryFn: () => dealerPortalService.getCompany(),
  });

  const logoPreviewMediaId = company.data?.logoPreviewMediaId ?? null;
  const companyLogoUrl = useQuery({
    queryKey: [DEALER_PORTAL_KEY, "company-logo-url", logoPreviewMediaId],
    queryFn: () => MediaService.getDownloadUrl(logoPreviewMediaId!, 3600),
    enabled: !!logoPreviewMediaId,
    staleTime: 50 * 60 * 1000,
  });

  const colleagues = useQuery({
    queryKey: [DEALER_PORTAL_KEY, "colleagues"],
    queryFn: () => dealerPortalService.getColleagues(),
  });

  const customers = useQuery({
    queryKey: [DEALER_PORTAL_KEY, "customers"],
    queryFn: () => dealerPortalService.getCustomers(),
  });

  const customersPaginated = useQuery({
    queryKey: [DEALER_PORTAL_KEY, "customers-paginated", customersPage, customersPageSize, customersSearch, customersRole, customersType],
    queryFn: () =>
      dealerPortalService.getCustomersPaginated({
        search: customersSearch != null && customersSearch !== "" ? customersSearch : undefined,
        page: customersPage,
        limit: customersPageSize ?? DEFAULT_CUSTOMERS_PAGE_SIZE,
        role: customersRole ?? undefined,
        customerType: customersType ?? undefined,
      }),
    enabled: true,
  });

  const vehicles = useQuery({
    queryKey: [DEALER_PORTAL_KEY, "vehicles", vehiclesSearch],
    queryFn: () =>
      dealerPortalService.getVehicles(
        vehiclesSearch != null && vehiclesSearch !== "" ? vehiclesSearch : undefined
      ),
  });

  const vehiclesPaginated = useQuery({
    queryKey: [DEALER_PORTAL_KEY, "vehicles-paginated", vehiclesSearch, vehiclesStatus, vehiclesPage, vehiclesPageSize],
    queryFn: () =>
      dealerPortalService.getVehiclesPaginated({
        search: vehiclesSearch != null && vehiclesSearch !== "" ? vehiclesSearch : undefined,
        status: vehiclesStatus != null && vehiclesStatus !== "" ? vehiclesStatus : undefined,
        page: vehiclesPage,
        limit: vehiclesPageSize,
      }),
    enabled: true,
  });

  const journeyCounts = useQuery({
    queryKey: [DEALER_PORTAL_KEY, "journey-counts"],
    queryFn: () => dealerPortalService.getJourneyCounts(),
  });

  const journeys = useQuery({
    queryKey: [DEALER_PORTAL_KEY, "journeys"],
    queryFn: () => dealerPortalService.getJourneys(),
    enabled: journeysEnabled,
  });

  // Queries med parametrar (enabled utifrån options)
  const customer = useQuery({
    queryKey: [DEALER_PORTAL_KEY, "customer", customerId],
    queryFn: () => dealerPortalService.getCustomer(customerId!),
    enabled: !!customerId,
  });

  const vehicle = useQuery({
    queryKey: [DEALER_PORTAL_KEY, "vehicle", vehicleId],
    queryFn: () => dealerPortalService.getVehicle(vehicleId!),
    enabled: !!vehicleId,
  });

  const vehicleCarTests = useQuery({
    queryKey: [DEALER_PORTAL_KEY, "vehicle-car-tests", vehicleId],
    queryFn: () => dealerPortalService.getVehicleCarTests(vehicleId!),
    enabled: !!vehicleId,
  });

  const journey = useQuery({
    queryKey: [DEALER_PORTAL_KEY, "journey", journeyId],
    queryFn: () => dealerPortalService.getJourney(journeyId!),
    enabled: !!journeyId,
  });

  const journeysByStatus = useQuery({
    queryKey: [DEALER_PORTAL_KEY, "journeys", journeyStatus, journeyPage, journeyPageSize],
    queryFn: () =>
      dealerPortalService.getJourneysByStatus(journeyStatus!, journeyPage, journeyPageSize),
    enabled: !!journeyStatus && journeyPage >= 1 && journeyPageSize >= 1,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [DEALER_PORTAL_KEY] });

  // Mutations
  const createCustomer = useMutation({
    mutationFn: (body: {
      name: string;
      email?: string;
      phone?: string;
      customerType?: "PRIVATE" | "BUSINESS" | "DEALER" | "private" | "business" | "dealer";
      personalNumber?: string;
      street?: string;
      postalCode?: string;
      city?: string;
      country?: string;
      companyConnection?: string;
      isBuyer?: boolean;
      isSeller?: boolean;
    }) => dealerPortalService.createCustomer(body),
    onSuccess: invalidate,
  });

  const updateCustomer = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: {
        name?: string;
        email?: string;
        phone?: string;
        customerType?: "PRIVATE" | "BUSINESS" | "DEALER" | "private" | "business" | "dealer";
        personalNumber?: string;
        street?: string;
        postalCode?: string;
        city?: string;
        country?: string;
        companyConnection?: string;
        isBuyer?: boolean;
        isSeller?: boolean;
      };
    }) => dealerPortalService.updateCustomer(id, body),
    onSuccess: invalidate,
  });

  const updateCustomerVehicleConnections = useMutation({
    mutationFn: ({ customerId, vehicleIds }: { customerId: string; vehicleIds: string[] }) =>
      dealerPortalService.updateCustomerVehicleConnections(customerId, vehicleIds),
    onSuccess: invalidate,
  });

  const deleteCustomer = useMutation({
    mutationFn: (id: string) => dealerPortalService.deleteCustomer(id),
    onSuccess: (_, deletedId) => {
      queryClient.removeQueries({ queryKey: [DEALER_PORTAL_KEY, "customer", deletedId] });
      queryClient.invalidateQueries({ queryKey: [DEALER_PORTAL_KEY, "customers"] });
      queryClient.invalidateQueries({ queryKey: [DEALER_PORTAL_KEY, "customers-paginated"] });
    },
  });

  const addCustomerNote = useMutation({
    mutationFn: ({ customerId, content }: { customerId: string; content: string }) =>
      dealerPortalService.addCustomerNote(customerId, content),
    onSuccess: (_, { customerId }) => {
      queryClient.invalidateQueries({ queryKey: [DEALER_PORTAL_KEY, "customer", customerId] });
      queryClient.invalidateQueries({ queryKey: [DEALER_PORTAL_KEY, "customers"] });
    },
  });

  const createCustomerDocument = useMutation({
    mutationFn: (customerId: string) => dealerPortalService.createCustomerDocument(customerId),
    onSuccess: (_, customerId) => {
      queryClient.invalidateQueries({ queryKey: [DEALER_PORTAL_KEY, "customer", customerId] });
    },
  });

  const uploadCustomerDocument = useMutation({
    mutationFn: ({ customerId, file }: { customerId: string; file: File }) =>
      dealerPortalService.uploadCustomerDocument(customerId, file),
    onSuccess: (_, { customerId }) => {
      queryClient.invalidateQueries({ queryKey: [DEALER_PORTAL_KEY, "customer", customerId] });
      queryClient.invalidateQueries({ queryKey: [DEALER_PORTAL_KEY, "customers"] });
    },
  });

  const deleteCustomerDocument = useMutation({
    mutationFn: ({ customerId, mediaId }: { customerId: string; mediaId: string }) =>
      dealerPortalService.deleteCustomerDocument(customerId, mediaId),
    onSuccess: (_, { customerId }) => {
      queryClient.invalidateQueries({ queryKey: [DEALER_PORTAL_KEY, "customer", customerId] });
      queryClient.invalidateQueries({ queryKey: [DEALER_PORTAL_KEY, "customers"] });
    },
  });

  const createVehicle = useMutation({
    mutationFn: (body: {
      regNr: string;
      make: string;
      model: string;
      year: number;
      status?: string;
      parkingSpot?: string | null;
      variant?: string | null;
      condition?: string | null;
      mileage?: number | null;
      engine?: string | null;
      transmission?: string | null;
      bodyType?: string | null;
      color?: string | null;
      fuelType?: string | null;
      horsepower?: number | null;
      engineSize?: string | null;
      vin?: string | null;
      equipment?: string[];
    }) => dealerPortalService.createVehicle(body),
    onSuccess: invalidate,
  });

  const updateVehicle = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: {
        regNr?: string;
        make?: string;
        model?: string;
        year?: number;
        status?: string | null;
        parkingSpot?: string | null;
        variant?: string | null;
        condition?: string | null;
        mileage?: number | null;
        engine?: string | null;
        transmission?: string | null;
        bodyType?: string | null;
        color?: string | null;
        fuelType?: string | null;
        horsepower?: number | null;
        engineSize?: string | null;
        vin?: string | null;
        equipment?: string[];
      };
    }) => dealerPortalService.updateVehicle(id, body),
    onSuccess: invalidate,
  });

  const deleteVehicle = useMutation({
    mutationFn: (id: string) => dealerPortalService.deleteVehicle(id),
    onSuccess: invalidate,
  });

  const uploadVehicleDocument = useMutation({
    mutationFn: ({
      vehicleId,
      file,
      imageCategory,
      documentCategory,
    }: {
      vehicleId: string;
      file: File;
      imageCategory?: string | null;
      documentCategory?: string | null;
    }) =>
      dealerPortalService.uploadVehicleDocument(vehicleId, file, {
        imageCategory: imageCategory ?? undefined,
        documentCategory: documentCategory ?? undefined,
      }),
    onSuccess: (_, { vehicleId }) => {
      queryClient.invalidateQueries({ queryKey: [DEALER_PORTAL_KEY, "vehicle", vehicleId] });
      queryClient.invalidateQueries({ queryKey: [DEALER_PORTAL_KEY, "vehicles"] });
    },
  });

  const deleteVehicleDocument = useMutation({
    mutationFn: ({ vehicleId, mediaId }: { vehicleId: string; mediaId: string }) =>
      dealerPortalService.deleteVehicleDocument(vehicleId, mediaId),
    onSuccess: (_, { vehicleId }) => {
      queryClient.invalidateQueries({ queryKey: [DEALER_PORTAL_KEY, "vehicle", vehicleId] });
      queryClient.invalidateQueries({ queryKey: [DEALER_PORTAL_KEY, "vehicles"] });
    },
  });

  const addVehicleComment = useMutation({
    mutationFn: ({
      vehicleId,
      content,
      mentions,
    }: {
      vehicleId: string;
      content: string;
      mentions?: string[];
    }) => dealerPortalService.addVehicleComment(vehicleId, { content, mentions }),
    onSuccess: (_, { vehicleId }) => {
      queryClient.invalidateQueries({ queryKey: [DEALER_PORTAL_KEY, "vehicle", vehicleId] });
      queryClient.invalidateQueries({ queryKey: [DEALER_PORTAL_KEY, "vehicles"] });
      // So customer cards’ notes/history counts stay in sync when comments are added from vehicle card
      queryClient.invalidateQueries({ queryKey: [DEALER_PORTAL_KEY, "customers-paginated"] });
      queryClient.invalidateQueries({ queryKey: [DEALER_PORTAL_KEY, "customers"] });
    },
  });

  const updateVehicleDocumentComment = useMutation({
    mutationFn: ({
      vehicleId,
      documentId,
      comment,
    }: { vehicleId: string; documentId: string; comment: string | null }) =>
      dealerPortalService.updateVehicleDocumentComment(vehicleId, documentId, comment),
    onSuccess: (_, { vehicleId }) => {
      queryClient.invalidateQueries({ queryKey: [DEALER_PORTAL_KEY, "vehicle", vehicleId] });
    },
  });

  const addVehicleServiceDetail = useMutation({
    mutationFn: ({
      vehicleId,
      data,
    }: {
      vehicleId: string;
      data: { date: string; mileage: number; serviceType: string; notes?: string | null };
    }) => dealerPortalService.addVehicleServiceDetail(vehicleId, data),
    onSuccess: (_, { vehicleId }) => {
      queryClient.invalidateQueries({ queryKey: [DEALER_PORTAL_KEY, "vehicle", vehicleId] });
    },
  });

  const removeVehicleServiceDetail = useMutation({
    mutationFn: ({ vehicleId, detailId }: { vehicleId: string; detailId: string }) =>
      dealerPortalService.removeVehicleServiceDetail(vehicleId, detailId),
    onSuccess: (_, { vehicleId }) => {
      queryClient.invalidateQueries({ queryKey: [DEALER_PORTAL_KEY, "vehicle", vehicleId] });
    },
  });

  const updateVehicleServiceDetail = useMutation({
    mutationFn: ({
      vehicleId,
      detailId,
      data,
    }: {
      vehicleId: string;
      detailId: string;
      data: { date: string; mileage: number; serviceType: string; notes?: string | null };
    }) => dealerPortalService.updateVehicleServiceDetail(vehicleId, detailId, data),
    onSuccess: (_, { vehicleId }) => {
      queryClient.invalidateQueries({ queryKey: [DEALER_PORTAL_KEY, "vehicle", vehicleId] });
    },
  });

  const createJourney = useMutation({
    mutationFn: (body: {
      customerId: string;
      vehicleId?: string | null;
      status?: string;
      priority?: string;
      assignedActorId?: string | null;
      followUpNote?: string | null;
      followUpAt?: string | null;
    }) => dealerPortalService.createJourney(body),
    onSuccess: invalidate,
  });

  const updateJourney = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: {
        status?: string;
        priority?: string;
        assignedActorId?: string | null;
        vehicleId?: string | null;
        followUpNote?: string | null;
        followUpAt?: string | null;
        statusChangeReason?: string;
      };
    }) => dealerPortalService.updateJourney(id, body),
    onSuccess: invalidate,
  });

  const addJourneyComment = useMutation({
    mutationFn: ({
      id,
      content,
      mentions,
    }: {
      id: string;
      content: string;
      mentions?: string[];
    }) => dealerPortalService.addJourneyComment(id, { content, mentions }),
    onSuccess: (_, { id }) => {
      invalidate();
      // Journey comment creates a customer note – ensure customer list counts update
      queryClient.invalidateQueries({ queryKey: [DEALER_PORTAL_KEY, "customers"] });
      queryClient.invalidateQueries({ queryKey: [DEALER_PORTAL_KEY, "customers-paginated"] });
      queryClient.removeQueries({ queryKey: [DEALER_PORTAL_KEY, "journey", id] });
      void queryClient.refetchQueries({ queryKey: [DEALER_PORTAL_KEY, "journeys"] });
      void queryClient.refetchQueries({ queryKey: [DEALER_PORTAL_KEY, "journey-counts"] });
    },
  });

  const deleteJourney = useMutation({
    mutationFn: (id: string) => dealerPortalService.deleteJourney(id),
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: [DEALER_PORTAL_KEY, "journey", id] });
      // Refetch list + counts only (not full invalidate, to avoid refetching the deleted journey → 404)
      void queryClient.refetchQueries({ queryKey: [DEALER_PORTAL_KEY, "journeys"] });
      void queryClient.refetchQueries({ queryKey: [DEALER_PORTAL_KEY, "journey-counts"] });
    },
  });

  const updateCompanyLogo = useMutation({
    mutationFn: async (body: { logoPreviewMediaId?: string | null; logoFullSizeMediaId?: string | null }) => {
      const result = await dealerPortalService.updateCompanyLogo(body);
      // Remove all old company-logo-url queries (they may reference deleted Media)
      queryClient.removeQueries({ queryKey: [DEALER_PORTAL_KEY, "company-logo-url"] });
      await queryClient.refetchQueries({ queryKey: [DEALER_PORTAL_KEY, "company"] });
      return result;
    },
  });

  return {
    // Queries
    company,
    companyLogoUrl: companyLogoUrl.data ?? null,
    colleagues,
    customers,
    customersPaginated,
    vehicles,
    vehiclesPaginated,
    journeyCounts,
    journeys,
    customer,
    vehicle,
    vehicleCarTests,
    journey,
    journeysByStatus,
    // Mutations
    createCustomer,
    updateCustomer,
    updateCustomerVehicleConnections,
    deleteCustomer,
    addCustomerNote,
    createCustomerDocument,
    uploadCustomerDocument,
    deleteCustomerDocument,
    createVehicle,
    updateVehicle,
    deleteVehicle,
    uploadVehicleDocument,
    deleteVehicleDocument,
    addVehicleComment,
    updateVehicleDocumentComment,
    addVehicleServiceDetail,
    updateVehicleServiceDetail,
    removeVehicleServiceDetail,
    createJourney,
    updateJourney,
    addJourneyComment,
    deleteJourney,
    updateCompanyLogo,
    // Imperative API for paginated/search (e.g. dropdowns)
    getCustomersPaginated: dealerPortalService.getCustomersPaginated,
    getVehiclesPaginated: dealerPortalService.getVehiclesPaginated,
    // Helpers
    invalidate,
  };
}
